import { useRef, useState, useCallback, useEffect } from "react";
import ContractEditor, { ContractEditorRef } from "../../components/contract-editor/ContractEditor";
import styles from './DocxEditor.module.css';
import { PlaceholdersPanel } from "../../components/placeholders-panel";
import { EditorOnboarding } from "../../components/editor-onboarding/EditorOnboarding";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../services/api";
import { FaFile, FaRoute } from "react-icons/fa";
import { ehPlanoGratuito, estaEmPeriodoDeTeste } from "../../services/seguranca";
import { useAppSelector } from "../../store/hooks";

export default function DocxEditor() {
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [documentHTML, setDocumentHTML] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasExistingDocument, setHasExistingDocument] = useState(false);
  const [startedBlank, setStartedBlank] = useState(false);
  const editorRef = useRef<ContractEditorRef | null>(null);
  const [nomeDocumento, setNomeDocumento] = useState<string>("");
  const [showVideo, setShowVideo] = useState(false);
  const [showTour, setShowTour] = useState(true);
  const token = useAppSelector(state => state.token);
  const logado = useAppSelector(state => state.logado);
  const expirado = useAppSelector(state => state.expirado);
  const bloqueadoPorInadimplencia = useAppSelector(state => state.bloqueadoPorInadimplencia);

  const location = useLocation();
  const idModeloContrato = (location.state as any)?.idModeloContrato;

  const history = useHistory();

  // A rota /editor-contrato fica fora do LocaSmartLayout (full-screen), entao os guards de
  // acesso do layout nao valem aqui e precisam ser replicados manualmente.
  useEffect(() => {
    if (!logado && !token) {
      history.replace('/login/acessobloqueado');
      return;
    }
    if (expirado) {
      history.replace('/login');
      return;
    }
    if (bloqueadoPorInadimplencia) {
      history.replace('/renovar');
      return;
    }
    if (ehPlanoGratuito(token)) {
      history.push('/modelo-contrato');
      setTimeout(() => {
        toast.info('Apenas contas no plano Premium podem gerar contratos automáticos.');
      }, 300);
    }
  }, [logado, token, expirado, bloqueadoPorInadimplencia])

  // Buscar documento existente
  useEffect(() => {
    const fetchDocument = async () => {
      if (!idModeloContrato) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const response = await api.get(`/ModeloContrato/${idModeloContrato}`);
        setNomeDocumento(`Modelo de Contrato: ${response.data.titulo}`);

        if (response.data && response.data.documento) {
          const presignedUrl = response.data.documento;
          const fileResponse = await fetch(presignedUrl);
          if (fileResponse.ok) {
            const blob = await fileResponse.blob();
            const file = new File([blob], `modelo-contrato-${idModeloContrato}.docx`, {
              type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
            });
            setDocumentFile(file);
            setHasExistingDocument(true);
          } else {
            throw new Error(`Erro ao baixar arquivo: ${fileResponse.status}`);
          }
        }
      } catch (error: any) {
        if (error.response?.status !== 404) {
          toast.error("Erro ao carregar documento existente");
          console.error("Erro ao buscar documento:", error);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchDocument();
  }, [idModeloContrato]);

  const handleContentChange = useCallback((html: string) => {
    setDocumentHTML(html);
  }, []);

  const handleFileUpload = (file: File) => {
    setDocumentFile(file);
    setHasExistingDocument(false);
  };

  const handleReplaceDocument = () => {
    setDocumentFile(null);
    setHasExistingDocument(false);
    setStartedBlank(false);
  };

  const saveDocumentToServer = async () => {
    if (!editorRef.current || !idModeloContrato) {
      toast.error('Editor não está pronto ou ID do modelo não encontrado');
      return;
    }

    setIsSaving(true);

    try {
      const result = await editorRef.current.exportDocx();

      if (!result || !(result instanceof Blob)) {
        throw new Error('Exportação não retornou um arquivo válido');
      }

      const formData = new FormData();
      formData.append("documento", result, `modelo-contrato-${idModeloContrato}.docx`);

      await api.put(
        `/ModeloContrato/SalvarDocumento/${idModeloContrato}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success('Documento salvo com sucesso!');
      history.push('/modelo-contrato');

      setHasExistingDocument(true);

      const file = new File([result], `modelo-contrato-${idModeloContrato}.docx`, {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      setDocumentFile(file);
    } catch (error) {
      console.error('Erro ao salvar documento:', error);
      if (error instanceof Error) {
        toast.error(`Erro ao salvar: ${error.message}`);
      } else {
        toast.error('Erro desconhecido ao salvar documento');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const exportDocx = async () => {
    if (!editorRef.current) return;

    try {
      const result = await editorRef.current.exportDocx();

      if (result instanceof Blob) {
        const url = URL.createObjectURL(result);
        const link = document.createElement("a");
        link.href = url;
        link.download = "documento-editado.docx";
        link.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error("Error exporting document:", error);
      toast.error('Erro ao exportar documento');
    }
  };

  const handleExportOrSave = async () => {
    if (idModeloContrato) {
      await saveDocumentToServer();
    } else {
      await exportDocx();
    }
  };

  const handleInsertPlaceholder = useCallback((placeholder: string) => {
    if (!editorRef.current) return;
    editorRef.current.insertVariable(placeholder);
  }, []);

  const handleInsertCustomPlaceholder = useCallback((key: string) => {
    if (!editorRef.current) return;
    editorRef.current.insertCustomVariable(key);
  }, []);

  const handleOpenHelp = () => {
    window.open("https://youtu.be/K--okUsfQxI", "_blank");
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Carregando documento...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.container}>
        {!documentFile && !startedBlank ? (
          <UploadView
            onFileUpload={handleFileUpload}
            onStartBlank={() => setStartedBlank(true)}
            hasIdModelo={!!idModeloContrato}
            showVideo={showVideo}
            setShowVideo={setShowVideo}
          />
        ) : (
          <div className={styles.editorWithSidebar}>
            <div data-tour="placeholders">
              <PlaceholdersPanel
                onInsertPlaceholder={handleInsertPlaceholder}
                onInsertCustomPlaceholder={handleInsertCustomPlaceholder}
                documentContent={documentHTML}
              />
            </div>

            <div className={styles.editorContainer}>
              <div className={styles.toolbar}>
                <button
                  onClick={() => {
                    history.push('/modelo-contrato')
                  }}
                  className={styles.toolbarButton}
                  disabled={isLoading || isSaving}
                >
                  ← Voltar
                </button>
                <h2>{nomeDocumento}</h2>
                <div className={styles.toolbarActions}>
                  <button
                    onClick={handleReplaceDocument}
                    className={styles.replaceButton}
                    disabled={isLoading || isSaving}
                  >
                    <FaFile />
                    Trocar Documento
                  </button>
                  {idModeloContrato && (
                    <button
                      onClick={exportDocx}
                      className={styles.replaceButton}
                      disabled={isLoading || isSaving}
                      title="Baixa o DOCX gerado pelo editor (para inspeção)"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19 9H15V3H9V9H5L12 16L19 9Z" fill="currentColor" />
                        <path d="M5 18V20H19V18H5Z" fill="currentColor" />
                      </svg>
                      Baixar DOCX
                    </button>
                  )}
                  <button
                    onClick={() => setShowTour(true)}
                    className={styles.helpButton}
                    title="Tour do Editor"
                  >
                    <FaRoute />
                    Tour do Editor
                  </button>
                  <button
                    onClick={handleOpenHelp}
                    className={styles.helpButton}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 19H11V17H13V19ZM15.07 11.25L14.17 12.17C13.45 12.9 13 13.5 13 15H11V14.5C11 13.4 11.45 12.4 12.17 11.67L13.41 10.41C13.78 10.05 14 9.55 14 9C14 7.9 13.1 7 12 7C10.9 7 10 7.9 10 9H8C8 6.79 9.79 5 12 5C14.21 5 16 6.79 16 9C16 9.88 15.64 10.68 15.07 11.25Z" fill="currentColor" />
                    </svg>
                    Entenda como funciona
                  </button>
                  <button
                    onClick={handleExportOrSave}
                    className={styles.exportButton}
                    disabled={isLoading || isSaving}
                    data-tour="save-button"
                  >
                    {isSaving ? (
                      <div className={styles.spinnerSmall}></div>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19 9H15V3H9V9H5L12 16L19 9Z" fill="currentColor" />
                        <path d="M5 18V20H19V18H5Z" fill="currentColor" />
                      </svg>
                    )}
                    {idModeloContrato ? 'Salvar Documento' : 'Exportar DOCX'}
                  </button>
                </div>
              </div>

              <ContractEditor
                ref={editorRef}
                document={documentFile}
                onContentChange={handleContentChange}
              />
            </div>

            <EditorOnboarding
              active={showTour}
              onFinish={() => setShowTour(false)}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function UploadView({
  onFileUpload,
  onStartBlank,
  hasIdModelo,
  showVideo,
  setShowVideo
}: {
  onFileUpload: (file: File) => void;
  onStartBlank: () => void;
  hasIdModelo: boolean;
  showVideo: boolean;
  setShowVideo: (show: boolean) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      onFileUpload(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file && file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      onFileUpload(file);
    }
  };

  return (
    <div className={styles.uploadContainer}>
      <div
        className={`${styles.uploadArea} ${isDragging ? styles.dragging : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className={styles.uploadContent}>
          <div className={styles.uploadIcon}>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M16 13H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M16 17H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M10 9H9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <h2>
            {hasIdModelo
              ? "Vamos começar: Informe o seu modelo de contrato"
              : "Arraste e solte um arquivo DOCX aqui"}
          </h2>
          <p>Clique para selecionar um arquivo ou arraste um</p>
          <div className={styles.fileTypes}>Apenas arquivos .docx são suportados</div>
        </div>
      </div>

      <div className={styles.uploadActions}>
        <button
          onClick={(e) => { e.stopPropagation(); onStartBlank(); }}
          className={styles.videoToggleButton}
          style={{ backgroundColor: '#1F467E', color: 'white', border: 'none' }}
        >
          <FaFile size={14} />
          Iniciar em branco
        </button>
        <button
          onClick={() => setShowVideo(!showVideo)}
          className={styles.videoToggleButton}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 16.5L16 12L10 7.5V16.5Z" fill="currentColor" />
            <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z" fill="currentColor" />
          </svg>
          {showVideo ? 'Ocultar Vídeo' : 'Como funciona?'}
        </button>
      </div>

      {showVideo && (
        <div className={styles.videoContainer}>
          <iframe width="560" height="315" src="https://www.youtube.com/embed/K--okUsfQxI?si=O8jPITDHvuMgsPjY" title="YouTube video player" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" allowFullScreen></iframe>
        </div>
      )}

      <input
        type="file"
        ref={fileInputRef}
        accept=".docx, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
    </div>
  );
}
